import type { DrugStatusId } from '@/shared/constants/nomenclature-status.constants';
import type { RecordStatusId } from '@/shared/constants/record-status.constants.ts';
import { httpClient } from '@/shared/lib/axios';

import type { PaginatedList, PaginationParams } from '../types/pagination.types';
import type { Brand } from './brands.types';
import type {
  BestOffersResponse,
  DistributorLoadOffersResponse,
  DistributorOffersResponse,
  Drug,
  DrugDosage,
  DrugImage,
  DrugImageInfo,
  DrugInfo,
  DrugInstructionForPharmacist,
  DrugOfferInfo,
  DrugOrder,
  DrugURLResources,
  Instruction,
  ReceptionFeature,
} from './drugs.types';

export type SearchSettingAlias =
  | 'distributors'
  | 'payment_types'
  | 'expire_date'
  | 'special_price'
  | 'without_offers'
  | 'dorim_promo'
  | 'yearly_interest';

export type FetchDrugImagesParams = {
  drug_id: number;
};
export type FetchInstructionParams = {
  drug_id: number;
};

export type DrugsSearchParams = PaginationParams & {
  query?: string;
  starts_with?: string;
  starts_with_digit?: boolean;
  distributor_ids?: number[];
  settings?: SearchSettingAlias[];
  share_only?: boolean;
  has_recommended_promo?: boolean;
  analogue_id?: number;
  drug_id?: number;
  drug_ids?: number[];
  record_status_ids?: DrugStatusId | DrugStatusId[] | null;
};

export type DrugFormData = {
  name?: string;
  category_id?: number;
  maker_id?: number;
  measure_unit_id?: number;
  vat_id?: number;

  form?: string;
  number?: number;
  class_id?: number;
  inn_id?: number;
  ikpu?: string;
  has_marks?: boolean;
  package_code?: number;
  package_name?: string;
  blister_count?: number;
  factory_package?: number;
  barcodes?: string[];
  barcode?: number;
  is_receipt?: boolean;

  maker_name?: string;
  country_name?: string;

  description?: string;

  record_status_id?: RecordStatusId;

  license_holder_id?: number;
  release_form_id?: number;
  category_dictionary_id?: number;
  url_resources?: DrugURLResources;
  marketing_org_id?: number;
  brand?: string;
  registration_number?: string;
  registration_start_date?: string;
  registration_end_date?: string;
  brand_id?: Brand['id'];
  type?: number;
};

export type DrugsDeleteFormData = {
  ids: number[];
};

type DrugStatuses = {
  record_status_ids: RecordStatusId[];
};

type UpdateDosage = {
  position: number;
  inn_id: number;
  measure_unit_id: number;
  dosage: number;
};

export type UpdateDosagesParams = {
  dosages: UpdateDosage[];
};

export type DrugOffersParams = PaginationParams & {
  distributor_ids?: number[];
  settings?: SearchSettingAlias[];
  with_share?: boolean;
};

export type DistributorOffersParams = {
  limit?: number;
  offset?: number;
  query?: string;
  settings?: SearchSettingAlias[];
};

export type BestOffersParams = {
  limit?: number;
  offset?: number;
  query?: string;
  settings?: SearchSettingAlias[];
};

export type UpdateDrugImagesParams = {
  drug_id: number;
  images: DrugImageInfo[] | null;
};

export type UpdateInstructionParams = {
  drug_id: number;
  instruction: string;
};

export type CreateInstructionForPharmacistParams = DrugInstructionForPharmacist & {
  drugId: number;
};

export type ListDrugsParams = PaginationParams & {
  query?: string;
};

export type FetchDrugOrdersParams = PaginationParams & {
  drug_id: number;
  period_start?: string;
  period_end?: string;
};

export const DrugsApi = {
  listDrugs: async (params: ListDrugsParams) => {
    const { data } = await httpClient.get<PaginatedList<'items', Drug>>('drugs', {
      params,
    });

    return data;
  },

  searchDrugs: async (params: DrugsSearchParams) => {
    const { data } = await httpClient.get<PaginatedList<'drugs', DrugInfo>>('drugs/search', {
      params,
    });

    return data;
  },

  fetchDrugs: async (params: DrugsSearchParams) => {
    const { data } = await httpClient.get<PaginatedList<'items', Drug>>('nomenclature/drugs', {
      params,
    });

    return data;
  },

  fetchOffers: async (drugId: number, params?: DrugOffersParams) => {
    const { data } = await httpClient.get<DrugOfferInfo[]>(`drugs/${drugId}/offers`, {
      params,
    });

    return data;
  },

  fetchDistributorLoadOffers: async (distributorId: number) => {
    const { data } = await httpClient.get<DistributorLoadOffersResponse>(`drugs/${distributorId}/load-offers`);
    return data;
  },

  fetchDistributorOffers: async (distributorId: number, params?: DistributorOffersParams) => {
    const { data } = await httpClient.get<DistributorOffersResponse>(`drugs/by-distributor/${distributorId}/offers`, {
      params,
    });

    return data;
  },

  fetchBestOffers: async (params?: BestOffersParams) => {
    const { data } = await httpClient.get<BestOffersResponse>('drugs/drugs/best-offers', {
      params,
    });

    return data;
  },

  fetchDrug: async (drugId: number) => {
    const { data } = await httpClient.get<Drug>(`nomenclature/drugs/${drugId}`);

    return data;
  },

  fetchDrugStatusList: async (drugId: number) => {
    const { data } = await httpClient.get<DrugStatuses>(`nomenclature/drugs/${drugId}/available-statuses`);

    return data;
  },

  deleteDrugs: async (formData: DrugsDeleteFormData) => {
    await httpClient.delete<void>('nomenclature/drugs', {
      data: formData,
    });
  },

  updateDrug: async (drugId: number, formData: DrugFormData) => {
    const { data } = await httpClient.put<Drug>(`nomenclature/drugs/${drugId}`, formData);

    return data;
  },

  createDrug: async (formData: DrugFormData) => {
    const { data } = await httpClient.post<Drug>('nomenclature/drugs', formData);

    return data;
  },

  fetchImages: async ({ drug_id }: FetchDrugImagesParams) => {
    const { data } = await httpClient.get<PaginatedList<'items', DrugImage>>(`drugs/${drug_id}/images`);

    return data;
  },

  fetchDosages: async (drugId: number) => {
    const { data } = await httpClient.get<PaginatedList<'items', DrugDosage>>(`drugs/${drugId}/dosages`);

    return data;
  },

  updateDosages: async (drugId: number, params: UpdateDosagesParams) =>
    await httpClient.patch(`drugs/${drugId}/dosages`, params),

  fetchInstructionForPharmacist: async (drugId: number) => {
    const { data } = await httpClient.get<DrugInstructionForPharmacist>(`drugs/${drugId}/script`);

    return data;
  },

  updateImages: async ({ drug_id, images }: UpdateDrugImagesParams) => {
    const { data } = await httpClient.put<PaginatedList<'items', DrugImage>>(`drugs/${drug_id}/images`, { images });

    return data;
  },

  fetchInstruction: async ({ drug_id }: FetchInstructionParams) => {
    const { data } = await httpClient.get<Instruction>(`drugs/${drug_id}/instruction`);

    return data;
  },

  updateInstruction: ({ drug_id, ...params }: UpdateInstructionParams) =>
    httpClient.patch<void>(`drugs/${drug_id}/instruction`, params),

  fetchReceptionFeatures: async (drugId: number) => {
    const { data } = await httpClient.get<PaginatedList<'items', ReceptionFeature>>(
      `drugs/${drugId}/reception-features`,
    );

    return data;
  },

  updateReceptionFeatures: async (drugId: number, formData: ReceptionFeature[]) => {
    await httpClient.patch<void>(`drugs/${drugId}/reception-features`, { reception_features: formData });
  },

  createInstructionForPharmacist: ({ drugId, ...params }: CreateInstructionForPharmacistParams) =>
    httpClient.post<void>(`drugs/${drugId}/script`, params),

  fetchDrugOrders: async ({ drug_id, ...params }: FetchDrugOrdersParams) => {
    const { data } = await httpClient.get<PaginatedList<'items', DrugOrder>>(`drugs/${drug_id}/orders`, {
      params,
    });

    return data;
  },
};
